/**
 * Promoções do Mercado Livre — regras de cálculo.
 *
 * Sem React, sem MUI, sem fetch: só tipos e contas. É aqui que moram as regras de negócio que
 * vieram do painel HTML original (`scripts/promocoes_ml/gen_dashboard_unificado.py`). A página
 * só monta a tela.
 *
 * ┌─ A REGRA QUE MANDA EM TUDO ───────────────────────────────────────────────────────────┐
 * │ Nenhum número desta tela é margem MÉDIA. As duas margens — a do preço cheio e a do     │
 * │ preço com desconto — saem da mesma fórmula, cada uma medida NO PREÇO DA SUA PRÓPRIA    │
 * │ LINHA, com os dados fixos do anúncio na API:                                          │
 * │                                                                                        │
 * │     M.C. = preço − comissão − frete − CMV − imposto                                   │
 * │                                                                                        │
 * │ Decisão da Mariana em 17/09/2026, literal: "você não vai usar no painel margem de      │
 * │ contribuição média em lugar nenhum — o único dado que você vai usar média é taxa de    │
 * │ imposto".                                                                              │
 * │                                                                                        │
 * │ ⛔ Os campos `mc_atual_rs_30d` / `mc_atual_pct_30d` existem no JSON e são PROIBIDOS na │
 * │ interface: são margem média por unidade vendida. Não leia nenhum dos dois.             │
 * └────────────────────────────────────────────────────────────────────────────────────────┘
 *
 * Componente ausente NUNCA vira zero silencioso: sai da conta, entra em `faltas[]` e a linha é
 * pintada de laranja. Laranja ≠ vermelho — vermelho é margem ruim (resultado), laranja é número
 * em que não dá para confiar (dado faltando).
 */

// ============================================================================================
// TIPOS — espelham o data.json gerado por montar_dados.py. Veja specs/FORMATO_DO_DADO.md.
// ============================================================================================

/**
 * Base de imposto usada no cálculo. Ver `explicaBase()` e specs/REGRAS_DE_CALCULO.md §3.
 *
 * Desde 18/09/2026 (noite) as duas bases são as da skill `margem-ajustada-fiesta`:
 *   'ajustada' = MC ajustada (com créditos) — padrão da tela e base em que o motor decide.
 *                Campos sem sufixo no JSON (`impostos_sim`, `mc_cheio_*`, `tax_rate_ajustada`).
 *   'real'     = MC oficial (notas fiscais) — informativa. Campos com sufixo `_real`.
 * A planilha de alíquota por SKU ('fixa') SAIU da conta; fica só em `tax_rate_planilha`.
 */
export type BaseImposto = 'ajustada' | 'real'

/** Rótulos que a skill exige. Nunca escrever só "margem". */
export const ROTULO_BASE: Record<BaseImposto, string> = {
  ajustada: 'MC ajustada (com créditos)',
  real: 'MC oficial (notas fiscais)',
}

export type StatusPromo = 'candidate' | 'pending' | 'started' | 'finished'

export interface Promocao {
  id: string
  /**
   * Identificador da OFERTA deste item na campanha, como a API devolve em
   * /seller-promotions/items: "CANDIDATE-MLB…-n" antes de entrar, "OFFER-MLB…-n" depois.
   * O ML exige mandá-lo como `offer_id` na SMART/PRE_NEGOTIATED (POST e DELETE) — sem ele
   * responde 400 "Offer id is required" (100% das SMART do log do BI em 19–23/09/2026).
   */
  ref_id?: string | null
  type: string
  type_label: string
  sub_type: string | null
  status: StatusPromo
  status_label: string
  name: string
  original_price: number | null
  /** Preço que o painel simula. Ver `origem_preco` para saber de qual dos 3 caminhos veio. */
  preco_promo: number | null
  origem_preco: string | null
  min_discounted_price: number | null
  max_discounted_price: number | null
  suggested_discounted_price: number | null
  /** Quem banca o desconto. `split_origem` diz se é da API, confirmação humana, ou nada. */
  ml_rs: number | null
  ml_pct: number | null
  seller_rs: number | null
  seller_pct: number | null
  desc_total_rs: number | null
  desc_total_pct: number | null
  split_origem: string
  fixed_percentage: number | null
  start_date: string | null
  finish_date: string | null
  /**
   * Faixa de unidades que o ML exige na oferta. Só LIGHTNING traz (`stock: {min, max}` na API;
   * min = 5 em 291 de 321 medidas em 18/09/2026). O POST da relâmpago PRECISA dizer quantas
   * unidades entram — sem isso o ML não cria a oferta. Ausente nos outros tipos.
   */
  stock_min?: number | null
  stock_max?: number | null
  /** Comissão REAL: chamada a /sites/MLB/listing_prices no preço desta promoção. */
  comissao_sim: number | null
  origem_comissao: string | null
  /** Frete REAL: /users/{seller}/shipping_options/free com item_price = preço desta promoção. */
  frete_real: number | null
  origem_frete: string
  /** ⚠️ Média histórica, mantida só como referência. NÃO entra em conta nenhuma. */
  frete_medio_30d_historico: number | null
  cmv: number | null
  /** Imposto na base AJUSTADA (com créditos, regra da skill). */
  impostos_sim: number | null
  imposto_origem: string | null
  /** Imposto na base OFICIAL (bruta da nota). */
  impostos_sim_real: number | null
  imposto_origem_real: string | null
  /**
   * Parte do desconto que o ML banca, em R$ — só quando o split é REAL da API (`SMART` e
   * `PRE_NEGOTIATED`). Regra da Mariana, 18/09/2026: "a parte do meli da promoção é abatida da
   * taxa, aumentando minha margem". `preco_promo` é o preço do COMPRADOR, já líquido das duas
   * parcelas, então esta parte VOLTA como receita na M.C. É o `sale_fee.rebate` do billing, e em
   * `margens` já vem embutido em `marketplace_fees`. Nos outros tipos é null — não se inventa.
   */
  rebate_ml_rs: number | null
  rebate_origem: string | null
  /** Chave do card (type|name|id). Ausente em arquivo antigo / promoção "só status" relida antes de 21/10/2026 — use `chaveCampanha(p)`. */
  campanha_key?: string
  /** Relâmpago (21/10/2026): janela "AAAA-MM-DD HH:MM→HH:MM" lida da lista de itens da promoção; ausente = sem data na API. */
  janela?: string | null
  /** Diferença entre a M.C. desta promoção e a do preço cheio (calculada no backend de dados). */
  impacto_mc_rs?: number | null
  impacto_mc_pct?: number | null
}

export interface Anuncio {
  mlb: string
  titulo: string
  sku: string | null
  preco_atual: number
  thumbnail: string | null
  category_id: string
  estoque: number
  sold_quantity: number
  /** % de comissão da categoria no preço cheio (só para exibir). */
  comissao_pct_atual: number | null
  cmv: number | null
  cmv_data: string | null
  cmv_velho: boolean
  /** Qual das 3 fontes de custo venceu. Sempre mostrar na tela. */
  cmv_origem: string | null
  // 08/10/2026: custos dos componentes são o CHEIO da cmvs (sem rebate_percent).
  cmv_componentes: unknown[] | null
  /** 08/10/2026: item comprado com ST (NCM × classificacao_fiscal). Nesse caso `cmv` é a média ponderada 30d/90d. */
  cmv_st?: boolean
  /** CMV pela composição × custo cheio — igual a `cmv` fora do ST; no ST, serve para comparar com a média. */
  cmv_composicao?: number | null
  /**
   * CMV na base AJUSTADA. Só difere de `cmv` na Extrema (regime de MG): vem do fator `dif_cmv`
   * da coleção margens_ajustadas, venda a venda — não de um crédito fixo. `cmv_ajuste_motivo` diz.
   */
  cmv_ajustado: number | null
  cmv_ajuste_motivo: string | null
  pedidos_30d: number
  faturamento_30d: number
  /** Frete real da API no preço cheio. */
  frete_atual_api: number | null
  /** As duas alíquotas, em %, cada uma com a origem. */
  tax_rate_ajustada: number | null
  tax_rate_ajustada_origem: string | null
  /** true = medida nas vendas deste anúncio (30d). false = média ponderada da categoria (180d), rotulada. */
  tax_rate_ajustada_propria: boolean
  /** Parcelas do ajuste, em pontos percentuais da receita (só quando própria). */
  ajuste_pis_cofins_pp: number | null
  ajuste_incentivo_pp: number | null
  tax_rate_real: number | null
  tax_rate_real_origem: string | null
  /** Planilha de alíquota por SKU — SÓ informação (alerta de divergência). Não entra em M.C. */
  tax_rate_planilha: number | null
  tax_rate_planilha_propria: boolean
  /** M.C. no preço cheio, base AJUSTADA (com `cmv_ajustado`). */
  mc_cheio_rs: number | null
  mc_cheio_pct: number | null
  mc_cheio_faltas: string[]
  comissao_cheio_rs: number | null
  imposto_cheio_rs: number | null
  /** M.C. no preço cheio, base OFICIAL (com `cmv`). */
  mc_cheio_rs_real: number | null
  mc_cheio_pct_real: number | null
  mc_cheio_faltas_real: string[]
  imposto_cheio_rs_real: number | null
  /** Variações (margem do anúncio = pior variação). 0 quando o anúncio não tem variação. */
  n_variacoes: number
  n_variacoes_com_custo: number
  variacoes_faltando: number
  /** Da API do ML (coletar_atributos_extra.py), não de mongodb_api.products — ver specs. */
  date_created: string | null
  logistic_type: string | null
  /** 'Full' | 'Coleta' | 'Agência (drop-off)'. ⚠️ 'Flex' não existe nesta base. */
  logistica_label: string | null
  /** Curva ABC por faturamento de 30d. null = não vendeu no período (não é curva C). */
  curva: 'A' | 'B' | 'C' | null
  novo: boolean
  promocoes: Promocao[]
  n_promo_ativa: number
  n_promo_candidata: number
  /** id da promoção de maior M.C. simulada do anúncio (montar_dados.py). Sem seleção de campanha, é a linha da aba Promoções. */
  melhor_promo_id?: string | null

  // ⛔ PROIBIDOS NA INTERFACE (margem média). Não declarados de propósito para o TypeScript
  // reclamar se alguém tentar usar: mc_atual_rs_30d, mc_atual_pct_30d.
}

export interface Conta {
  conta: string
  amostra: string
  fetched_at: string
  tax_janela: string
  periodo_margem: { de: string; ate: string }
  ads: Anuncio[]
  /**
   * ⚠️ Agregado da COLETA COMPLETA (montar_dados.py, 02:30). A releitura da API (atualizar_status.py,
   * 3×/dia e a cada rodada) muda o `status` de cada promoção mas NÃO refaz esta lista — por isso os
   * cards da aba Promoções não a usam mais: ver `campanhasDaConta()`. Medido em 02/10/2026 na VPS:
   * "10.10" dizia 754 ativas no card e tinha 796 promoções `started` no mesmo arquivo.
   */
  campanhas: Campanha[]
  /** Quando o status/preço/estoque foi relido da API (atualizar_status.py). Ausente = só a coleta completa. */
  status_atualizado_em?: string | null
  status_atualizacao?: { anuncios: number; promocoes_mudadas: number; margens_completadas?: number; precos_estoques_mudados: number; erros: number; segundos: number; parcial: boolean } | null
  alertas_cmv_ausente: { mlb: string; titulo: string; sku: string | null; motivo: string }[]
  alertas_cmv_velho: { mlb: string; titulo: string; sku: string; cmv: number; data: string; dias: number }[]
  sem_venda_recente: number
  erros_api: { item_id: string; endpoint: string; status: number }[]
  imposto: {
    base_padrao: 'ajustada'
    n_ads: number
    /** Anúncios com alíquota ajustada medida nas próprias vendas / herdada da categoria / sem nenhuma. */
    n_ajustada_propria: number
    n_ajustada_categoria: number
    n_sem_aliquota: number
    /** Ajustes típicos da conta, em pp da receita (mediana entre os anúncios com venda). */
    ajuste_pis_cofins_mediano_pp: number | null
    /** Mediana do ajuste do regime de MG (Extrema), em pp; null na matriz. */
    ajuste_regime_mediano_pp?: number | null
    /** Compat: mesmo valor de `ajuste_regime_mediano_pp` (payload antigo só tem este). */
    ajuste_incentivo_mediano_pp: number | null
    regra_versao?: string | null
    /** ISO UTC de quando a coleção margens_ajustadas foi calculada. */
    ajustada_calculada_em?: string | null
    /** Só em payload antigo; o motor novo não emite mais. */
    tem_incentivo?: boolean
    incentivo?: { conta: string; icms_saida: number; credito_compra: number; st_nomes: string } | null
    aliq_pis_cofins: number
    fracao_nf: number
    /** Planilha por SKU — só informação. */
    fonte_planilha: { arquivo?: string; empresa?: string; skus?: number; natureza?: string }
    n_com_planilha_propria: number
    /** Mediana de (ajustada − planilha) na conta, em pp. */
    divergencia_tipica_pp: number | null
    divergencia_tipica_n: number
    limiar_alerta_pp: number
  }
  alertas_divergencia_imposto: DivergenciaImposto[]
  /** Margem acima de `mc_suspeita_pct` → conferir o CMV (regra dela, 18/09/2026). Já ordenado. */
  alertas_mc_suspeita: McSuspeita[]
  mc_suspeita_pct: number
  analise: {
    faturamento_30d_total: number
    curva_corte_a: number
    curva_corte_b: number
    dias_novo: number
    n_curva: { A: number; B: number; C: number }
    tem_atributos: boolean
    atributos_coletados_em: string | null
  }
}

export interface Campanha {
  key: string
  type: string
  type_label: string
  name: string
  start_date: string | null
  finish_date: string | null
  n_ativa: number
  n_candidata: number
  n_outro: number
  n_ads: number
}

/** Card de campanha derivado do status ATUAL das promoções (ver `campanhasDaConta`). */
export interface CampanhaViva extends Campanha {
  /** finish_date já passou: a campanha acabou (o ML às vezes ainda devolve `started` por horas). */
  encerrada: boolean
}

/**
 * Cards da aba Promoções calculados a partir das promoções de cada anúncio — a mesma fonte das
 * linhas da tabela e dos contadores Todos/Candidatos/Participantes. Antes vinham de
 * `conta.campanhas`, agregado só na coleta das 02:30: depois de uma releitura da API (ou do robô
 * ativar/tirar), o card ficava com o número velho e a tabela com o novo — "os números não batem"
 * (Mariana, 02/10/2026). Campanha com `finish_date` passada vem com `encerrada: true` para a tela
 * esconder (ou avisar, se ainda houver `started` nela).
 */
/** Chave do card da promoção. Prefere a gravada pela coleta; sem ela (arquivo antigo, "só status" relida antes
 *  de 21/10/2026) monta `type|name|id` — nunca `undefined`, que juntava 60 promoções de 7 tipos num card só. */
export function chaveCampanha(p: Pick<Promocao, 'type'> & Partial<Pick<Promocao, 'campanha_key' | 'name' | 'id'>>): string {
  return p.campanha_key ?? `${p.type}|${p.name || ''}|${p.id || ''}`
}

/** Tipos cujo corpo do POST o ML não documenta: a tela não manda POST às cegas (ela, 21/10/2026: "relâmpago smart é diferente"). */
export const TIPOS_SEM_POST = new Set(['SMART_LIGHTNING'])
export const MOTIVO_SEM_POST = 'Relâmpago Smart — tipo novo, aguardando regra'

/** "2026-10-21 12:00→00:00" → "21/10 12h–00h" (texto curto da janela da relâmpago). */
export function rotuloJanela(janela: string | null | undefined): string {
  if (!janela) return ''
  const [dia, horas] = janela.split(' ')
  const [ini, fim] = (horas ?? '').split('→')
  return dia && ini && fim ? `${dia.slice(8, 10)}/${dia.slice(5, 7)} ${ini.slice(0, 2)}h–${fim.slice(0, 2)}h` : janela
}

export function campanhasDaConta(c: Conta, hoje: Date = new Date()): CampanhaViva[] {
  const m = new Map<string, CampanhaViva & { adsSet: Set<string> }>()
  for (const a of c.ads) for (const p of a.promocoes) {
    const key = chaveCampanha(p)
    let x = m.get(key)
    if (!x) {
      x = { key, type: p.type, type_label: p.type_label, name: p.name || p.type_label, start_date: p.start_date, finish_date: p.finish_date,
        n_ativa: 0, n_candidata: 0, n_outro: 0, n_ads: 0, encerrada: false, adsSet: new Set() }
      m.set(key, x)
    }
    if (p.status === 'started') x.n_ativa++
    else if (p.status === 'candidate') x.n_candidata++
    else x.n_outro++
    x.adsSet.add(a.mlb)
  }
  const out: CampanhaViva[] = []
  for (const x of m.values()) {
    const fim = x.finish_date ? new Date(x.finish_date) : null
    const encerrada = !!fim && !Number.isNaN(fim.getTime()) && fim.getTime() < hoje.getTime()
    const { adsSet, ...resto } = x
    out.push({ ...resto, n_ads: adsSet.size, encerrada })
  }
  out.sort((a, b) => (b.n_ativa + b.n_candidata) - (a.n_ativa + a.n_candidata))
  return out
}

export interface DivergenciaImposto {
  mlb: string
  titulo: string
  sku: string | null
  tax_planilha: number
  tax_ajustada: number
  tax_oficial: number | null
  /** ajustada − planilha */
  diferenca_pp: number
  /** diferença − mediana da conta */
  desvio_pp: number
  pedidos_30d: number
}

/**
 * Regra da Mariana, 18/09/2026: "sempre que vc ver margens de contribuição acima de 25%
 * desconfie — certamente há erro no CMV" / "leve para a aba de análise manual esses casos".
 * Fica na aba Análise, com o CMV e a origem dele — é isso que precisa ser conferido.
 */
export interface McSuspeita {
  mlb: string
  titulo: string
  sku: string | null
  preco: number
  cmv: number | null
  cmv_ajustado: number | null
  cmv_origem: string | null
  cmv_data: string | null
  mc_cheio_pct: number | null
  mc_cheio_rs: number | null
  n_promos_acima: number
  n_promos_acima_ativas: number
  maior_mc_promo_pct: number | null
  pedidos_30d: number
  curva: 'A' | 'B' | 'C' | null
}

/**
 * Fila do MOTOR DE REGRAS (`coleta/motor_promocoes.py` → `fila_decisoes.json`), embutida no
 * mesmo .gz e, com o serviço no ar, buscada fresca em `/api/promocoes/fila` (o motor roda todo
 * dia; o .gz não). O motor só PROPÕE — nada é ativado sem clique. Ver specs/MOTOR_DE_REGRAS.md.
 */
export type AcaoMotor = 'entrar' | 'trocar' | 'relampago' | 'cupom' | 'sair_auto' | 'alertar' | 'bloqueado' | 'nada'

export interface CamadaDecisao {
  tipo: string
  rotulo: string
  acao: string
  margem: number | null
  unidades: number | null
  preco_promo: number | null
  promocao_id: string | null
  promocao_nome: string | null
  /** Cupom do grupo aplicado no preço desta camada (ela, 06/10/2026); null/ausente = sem cupom do grupo. */
  cupom_grupo?: CupomGrupoLinha | null
}

/**
 * Cupom do grupo com regra própria aplicado na linha (ela, 06/10/2026): a `margem` da linha já é
 * a do preço pago com o cupom (desconto real: % com teto em R$ e compra mínima da campanha).
 */
export type CupomGrupoLinha = {
  id: string; nome: string; pct: number; teto_rs: number | null; minimo_rs: number | null
  desconto_rs: number; preco_pago: number; margem_sem_cupom: number | null
  /** margem OFICIAL sem o cupom (revisão final, 06/10/2026); `margem_real_informativa` já vem com o cupom */
  margem_real_sem_cupom?: number | null
}

/** R$ no formato do motor (`_rs` do Python): "R$ 1.234,56", com espaço comum (o toLocaleString põe espaço duro). */
const rs2 = (v: number) => `R$ ${v.toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`

/** Mesmo texto do motor (`texto_cupom_grupo` em regras_promocoes.py). */
export function textoCupomGrupo(c: CupomGrupoLinha): string {
  if (c.preco_pago == null) return `cupom "${c.nome}" sem preço pago calculável (promoção sem preço)`
  if (!c.desconto_rs && c.minimo_rs != null && c.preco_pago < c.minimo_rs)
    return `cupom "${c.nome}" não vale abaixo da compra mínima de ${rs2(c.minimo_rs)}`
  const teto = c.teto_rs != null ? `teto ${rs2(c.teto_rs)}: ` : ''
  return `com cupom "${c.nome}" ${c.pct}% (${teto}−${rs2(c.desconto_rs || 0)} → paga ${rs2(c.preco_pago)})`
}

/** " · começa dd/mm" para promoção de campanha futura (ela, 06/10/2026); a data é a do fuso da API, como no motor. */
export function textoComeca(vigencia?: string | null, inicio?: string | null): string {
  if (vigencia !== 'futura') return ''
  return inicio && inicio.length >= 10 ? ` · começa ${inicio.slice(8, 10)}/${inicio.slice(5, 7)}` : ' · campanha futura'
}

export interface DecisaoMotor {
  mlb: string
  titulo: string
  /** Já executada depois da fila (marcada pelo serviço a partir do escritas_promo.log). */
  executada?: Executada
  sku: string | null
  acao: AcaoMotor
  motivo: string
  /** Margem na base de decisão (ajustada). */
  margem: number | null
  /** MC oficial (notas fiscais) — só informativa. */
  margem_real_informativa: number | null
  preco_atual: number
  preco_promo: number | null
  promocao_tipo: string | null
  promocao_nome: string | null
  promocao_id: string | null
  /** Campanha que já vale × futura (ela, 06/10/2026, vigência primeiro). Ausente em filas antigas. */
  vigencia?: 'vigente' | 'futura' | null
  /** `start_date` da promoção (ISO da API, com fuso); null = sem data. Ausente em filas antigas. */
  promocao_inicio?: string | null
  ml_banca_pct: number | null
  estoque: number
  pedidos_30d: number
  faturamento_30d: number
  curva: 'A' | 'B' | 'C' | null
  logistica: string | null
  impacto_rs: number | null
  excecao: boolean
  /** Id do grupo com regra própria que decidiu esta linha (ela, 05/10/2026); null = regra geral. */
  grupo: string | null
  /** Relâmpago diária (21/10/2026): janela da oferta, posição na lista fixa dela (0 = primeira) e grupo da lista. Ausentes em filas antigas. */
  janela?: string | null
  posicao_fila?: number | null
  lista_fixa?: string | null
  /** Cupom do grupo aplicado no preço (ela, 06/10/2026): `margem` já é a do preço com o cupom. Ausente em filas antigas. */
  cupom_grupo?: CupomGrupoLinha | null
  unidades: number | null
  troca_de: {
    promocao_tipo: string
    promocao_nome: string | null
    promocao_id: string | null
    preco_promo: number | null
    margem: number | null
    ml_banca_pct: number | null
    cupom_grupo?: CupomGrupoLinha | null
    vigencia?: 'vigente' | 'futura' | null
    promocao_inicio?: string | null
    origem_promo?: 'robo' | 'manual' | null
  } | null
  ganho_pp: number | null
  camadas: CamadaDecisao[]
  desconto_somado: { preco_final_real: number; desconto_total_pct: number; avisos?: string[] } | null
  /** Apareceu nesta rodada (não estava na anterior). */
  novo: boolean
  acao_anterior?: string | null
  /** Reprovada por alguém na tela (decisoes_humanas.jsonl); o motor esconde até a proposta mudar. */
  reprovada?: { quando: string | null; por: string; motivo: string }
  /** 25-26/10/2026: regra que gerou a linha (preco_alvo, rebate_novo, manual, troca_encarece, cupom_nao_liberado, rebate, fora_da_lista…). */
  regra?: string | null
  /** Origem do desconto individual desta linha: robô, manual ou nenhuma. */
  origem_promo?: 'robo' | 'manual' | null
  /** Preço que o comprador vê antes/depois da rodada. */
  preco_antes?: number | null
  preco_depois?: number | null
  /** Linha própria de cupom/relâmpago (camada), independente da decisão principal do anúncio. */
  camada?: boolean
  /** Caso estranho (margem não confiável): motivo. Aparece no bloco "Revisão humana" da aba Alertas. */
  revisao_humana?: string | null
  /** 01/10/2026: entrada que parou no teto de 20% de desconto; `preco_sem_teto` = o que a meta de margem permitiria */
  teto_20?: boolean
  preco_sem_teto?: number | null
}

/** Escrita já feita no ML depois da fila (do escritas_promo.log). A fila/painel vem da coleta das 02:30. */
export type Executada = { quando: string | null; acao: 'ativar' | 'remover'; http: number; ja_fora?: boolean }

export interface FilaConta {
  apelido: string
  conta: string
  n_ads: number
  resumo: Record<string, number>
  decisoes: DecisaoMotor[]
  n_novos: number
  n_reprovadas?: number
  /** Quando o status das promoções/preço/estoque foi relido da API (atualizar_status.py). null = só a coleta completa. */
  status_atualizado_em?: string | null
  status_atualizacao?: { anuncios: number; promocoes_mudadas: number; precos_estoques_mudados: number; erros: number; segundos: number; parcial: boolean } | null
}

/** Um passo da coleta completa (pipeline_completo.sh → coleta_status.json) — ela, 07/10/2026. */
export interface ColetaPasso {
  nome: string
  ok: boolean
  tentativas: number
  max_tentativas?: number | null
  segundos: number
  erro?: string | null
}

/** Estado da última coleta completa (coleta_status.json). ok null = rodando (ou morreu no meio). */
export interface ColetaStatus {
  inicio: string | null
  fim: string | null
  ok: boolean | null
  passo_atual: string | null
  passos: ColetaPasso[]
  fetched_at: string | null
  por: string | null
}

export interface FilaDecisoes {
  gerado_em: string
  base_de_decisao: BaseImposto
  regras: {
    margem_minima_entrar: number
    margem_saida_automatica: number
    margem_alerta: number
    margem_relampago: number | null
    /** Relâmpago (23/10/2026): conta inteira por padrão (true = PROMO_RELAMPAGO_SO_LISTA=1, só a lista fixa); ids dos grupos marcados. */
    relampago_so_lista_fixa?: boolean
    relampago_lista_fixa?: string[]
    /** Meta de margem das campanhas com seletor de desconto (DEAL, campanha do vendedor, desconto individual) — 30/09/2026. */
    margem_seletor?: number
    /** Quantas promoções de disputa podem estar ativas no mesmo anúncio (3). */
    max_ativas_disputa?: number
    /** Sem estoque mínimo desde 30/09/2026; fica só para filas antigas. */
    estoque_minimo_entrar?: number | null
    criterio_desempate: string[]
    alertar_so_mudancas: boolean
    /** REVISAO_MARGEM_ALTA do motor: true = margem > 25% segura o anúncio na Revisão humana. Desligada em 01/10/2026 (ela conferiu os 292: liquidação). */
    revisao_margem_alta?: boolean
    /** De onde vieram os pisos: padrão do código ou editados na tela (parametros_motor.json). */
    parametros?: { origem: string; atualizado_em: string | null; por: { email?: string; nome?: string | null } | null; aviso?: string }
    padrao_do_codigo?: Record<string, number | boolean | string[]>
    /** 25-26/10/2026: troca que encarece até X % (só com MC R$ maior), tolerância do preço-alvo, folga/desempate, desconto manual e cupons liberados. */
    troca_aumento_max_pct?: number
    tolerancia_acima_pct?: number
    folga_margem_rs?: number
    folga_margem_pct?: number
    desempate_rs?: number
    respeitar_desconto_manual?: boolean
    cupons_liberados?: string[]
  }
  excecoes_ativas?: unknown[]
  /** Grupos com regra própria que o motor aplicou (ela, 05/10/2026). */
  grupos?: GrupoFila[]
  /** Avisos dos grupos (ela, 06/10/2026), ex.: teto do cupom desconhecido — campanha fora do raw_campanhas.json. */
  grupos_aviso?: string[]
  /** Estado da última coleta completa (ela, 07/10/2026). null/ausente = sem registro. */
  coleta_status?: ColetaStatus | null
  excecoes_vencidas?: unknown[] | Record<string, unknown>
  executadas?: Array<{ user_id: string; mlb: string; promotion_type: string | null; promotion_id: string | null } & Executada>
  contas: Record<string, FilaConta>
}

const TZ_BR = 'America/Sao_Paulo'
const diaBR = (d: Date) => d.toLocaleDateString('pt-BR', { timeZone: TZ_BR, day: '2-digit', month: '2-digit' })
const horaBR = (d: Date) => d.toLocaleTimeString('pt-BR', { timeZone: TZ_BR, hour: '2-digit', minute: '2-digit' })
const dataValida = (iso: string | null | undefined) => {
  const d = iso ? new Date(iso) : null
  return d && !Number.isNaN(d.getTime()) ? d : null
}
/** "hoje 04:20" ou "06/10 04:20" (horário de Brasília). */
function diaHora(d: Date, agora: Date): string {
  return `${diaBR(d) === diaBR(agora) ? 'hoje' : diaBR(d)} ${horaBR(d)}`
}
function duracao(ms: number): string {
  const min = Math.max(0, Math.round(ms / 60000))
  return min < 60 ? `${min} min` : `${Math.floor(min / 60)}h${String(min % 60).padStart(2, '0')}`
}
/** Coleta "rodando" há mais que isto morreu no meio (VPS reiniciou, kill): vira alerta. Normal: ~1h50. */
export const COLETA_HORAS_INTERROMPIDA = 4

/**
 * Linha da coleta completa no cabeçalho do Robô — ela, 07/10/2026: "desenhe a correção definitiva". A coleta
 * das 02:30 de 07/10 não publicou e ninguém soube. Textos da spec §2.4 (pt-BR, horário de Brasília):
 *   ok      → `Coleta completa: OK hoje 04:20 (1h48)`
 *   alerta  → `⚠ Coleta completa FALHOU hoje 03:12 no passo "buscar Matriz" (tentativa 3/3) — M.C. ainda de 06/10`
 *   rodando → `Coleta completa rodando: passo "montar Extrema" (desde 02:30)`
 *   sem     → sem coleta_status.json (fila antiga ou pipeline antes de 07/10)
 * `fetchedAt` é o da base que a tela está mostrando (de quando é a M.C.).
 */
export function textoColeta(cs: ColetaStatus | null | undefined, fetchedAt: string, agora: Date): { texto: string; nivel: 'ok' | 'alerta' | 'rodando' | 'sem' } {
  if (!cs) return { texto: 'Coleta completa: sem registro de status (coleta_status.json)', nivel: 'sem' }
  const ini = dataValida(cs.inicio)
  const fim = dataValida(cs.fim)
  const base = dataValida(fetchedAt)
  const mcDe = base ? ` — M.C. ainda de ${diaBR(base)}` : ''
  if (cs.ok === true) {
    const quando = fim ? ` ${diaHora(fim, agora)}` : ''
    const dur = ini && fim ? ` (${duracao(fim.getTime() - ini.getTime())})` : ''
    const avisos = (cs.passos || []).filter((p) => !p.ok).map((p) => p.nome)
    return { texto: `Coleta completa: OK${quando}${dur}${avisos.length ? ` · aviso: ${avisos.join(', ')} falhou` : ''}`, nivel: 'ok' }
  }
  const passo = cs.passo_atual ? ` no passo "${cs.passo_atual}"` : ''
  if (cs.ok === false) {
    const p = [...(cs.passos || [])].reverse().find((x) => x.nome === cs.passo_atual && !x.ok)
    const tent = p ? ` (tentativa ${p.tentativas}${p.max_tentativas ? `/${p.max_tentativas}` : ''})` : ''
    return { texto: `⚠ Coleta completa FALHOU${fim ? ` ${diaHora(fim, agora)}` : ''}${passo}${tent}${mcDe}`, nivel: 'alerta' }
  }
  if (ini && agora.getTime() - ini.getTime() > COLETA_HORAS_INTERROMPIDA * 3600000) {
    return { texto: `⚠ Coleta completa interrompida${passo} (começou ${diaHora(ini, agora)})${mcDe}`, nivel: 'alerta' }
  }
  return { texto: `Coleta completa rodando${cs.passo_atual ? `: passo "${cs.passo_atual}"` : ''}${ini ? ` (desde ${horaBR(ini)})` : ''}`, nivel: 'rodando' }
}

export interface BasePromocoes {
  gerado_em: string
  contas: Record<string, Conta>
  fila?: FilaDecisoes
}

/** As duas contas do ML. Rodar sempre as duas e dizer qual está na tela. */
export const CONTAS = [
  { id: '777058595', nome: 'FIESTAPARTYFESTA', curto: 'Matriz', empresa: 'MATRIZ' },
  { id: '3131364517', nome: 'FIESTAPARTYHOME', curto: 'Extrema', empresa: 'EXTREMA' },
] as const

// ============================================================================================
// CÁLCULO DA MARGEM
// ============================================================================================

export interface ResultadoMC {
  rs: number | null
  pct: number | null
  /** O que não entrou na conta. Vazio = número confiável. Não-vazio = pintar de laranja. */
  faltas: string[]
}

const r2 = (n: number) => Math.round(n * 100) / 100
const r1 = (n: number) => Math.round(n * 10) / 10

/**
 * CMV do anúncio, considerando uma eventual correção digitada pela usuária.
 *
 * ⚠️ O valor digitado é CORREÇÃO DE LEITURA: vale só nesta tela e **não corrige a base**.
 * Deixe isso escrito na interface — foi pedido explícito dela.
 */
export function cmvDe(a: Anuncio, base: BaseImposto, cmvManual?: Map<string, number>): { v: number | null; manual: boolean; origem: string } {
  const m = cmvManual?.get(a.mlb)
  if (m != null) return { v: m, manual: true, origem: 'valor que você digitou (não corrige a base)' }
  // na base ajustada, o CMV vem do fator dif_cmv da coleção margens_ajustadas (venda a venda), não de crédito fixo
  if (base !== 'real' && a.cmv_ajustado != null && a.cmv_ajustado !== a.cmv) {
    return { v: a.cmv_ajustado, manual: false, origem: `${a.cmv_origem ?? 'sem custo'} · ajustado: ${a.cmv_ajuste_motivo ?? ''}` }
  }
  return { v: a.cmv, manual: false, origem: a.cmv_origem ?? 'sem custo' }
}

/** Imposto da promoção na base escolhida. */
export const impostoDaPromo = (p: Promocao, base: BaseImposto) =>
  base === 'real' ? p.impostos_sim_real : p.impostos_sim

/** Alíquota do anúncio (em %) na base escolhida. */
export const aliquotaDoAd = (a: Anuncio, base: BaseImposto) =>
  base === 'real' ? a.tax_rate_real : a.tax_rate_ajustada

export const origemImposto = (a: Anuncio, base: BaseImposto) =>
  base === 'real' ? a.tax_rate_real_origem : a.tax_rate_ajustada_origem

/**
 * M.C. de uma promoção. Usa os componentes REAIS já buscados na API para aquele preço —
 * comissão de listing_prices e frete de shipping_options/free. Não reconstrói nada por fórmula.
 *
 *     M.C. = preço + parte do ML abatida da taxa − comissão − frete − CMV − imposto
 *
 * A "parte do ML" (`rebate_ml_rs`) só existe quando a API informa quem banca (Smart e
 * Pré-negociada); nos outros tipos é null e não entra. Ver `Promocao.rebate_ml_rs`.
 */
export function mcDe(a: Anuncio, p: Promocao, base: BaseImposto, cmvManual?: Map<string, number>): ResultadoMC {
  const c = cmvDe(a, base, cmvManual)
  const imp = impostoDaPromo(p, base)
  const faltas: string[] = []
  if (p.preco_promo == null) faltas.push('preço da promoção')
  if (p.comissao_sim == null) faltas.push('comissão')
  if (p.frete_real == null) faltas.push('frete')
  if (c.v == null) faltas.push('custo')
  if (imp == null) faltas.push('imposto')

  if (p.preco_promo == null || p.comissao_sim == null || p.frete_real == null || c.v == null) {
    return { rs: null, pct: null, faltas }
  }
  const rs = r2(p.preco_promo + (p.rebate_ml_rs ?? 0) - p.comissao_sim - p.frete_real - c.v - (imp ?? 0))
  return { rs, pct: r1((rs / p.preco_promo) * 100), faltas }
}

/**
 * "M.C. hoje": a margem da promoção ATIVA do anúncio (a de menor preço, que é o que o comprador
 * vê; cupom fica fora). Pedido dela em 18/09/2026: o rebate "tem que refletir nos cálculos de
 * margem do anúncio também". Sem promoção ativa → null (a margem de hoje é a do preço cheio).
 */
export function mcHoje(a: Anuncio, base: BaseImposto, cmvManual?: Map<string, number>): { p: Promocao; m: ResultadoMC } | null {
  const ativas = a.promocoes.filter((p) => p.status === 'started' && p.type !== 'SELLER_COUPON_CAMPAIGN' && p.preco_promo != null)
  if (!ativas.length) return null
  const p = [...ativas].sort((x, y) => (x.preco_promo ?? 0) - (y.preco_promo ?? 0))[0]
  return { p, m: mcDe(a, p, base, cmvManual) }
}

/**
 * M.C. no preço cheio, na base escolhida. Já vem calculada do backend de dados — aqui só se
 * escolhe qual campo ler.
 *
 * Vantagem sobre a margem média que ela proibiu: existe para TODO anúncio, não só para quem
 * vendeu nos últimos 30 dias.
 */
export function mcCheioDe(a: Anuncio, base: BaseImposto): ResultadoMC {
  return base === 'real'
    ? { rs: a.mc_cheio_rs_real, pct: a.mc_cheio_pct_real, faltas: a.mc_cheio_faltas_real ?? [] }
    : { rs: a.mc_cheio_rs, pct: a.mc_cheio_pct, faltas: a.mc_cheio_faltas ?? [] }
}

// ============================================================================================
// MEMÓRIA DE CÁLCULO — o que aparece ao passar o mouse na M.C.
// ============================================================================================

export interface ParcelaCalculo {
  rotulo: string
  valor: number | null
  /** '' para o preço, '−' para o que é subtraído. */
  sinal: string
  origem: string
}

export interface MemoriaCalculo {
  titulo: string
  parcelas: ParcelaCalculo[]
  total: ResultadoMC
  /** Frase de rodapé dizendo qual base de imposto está valendo. */
  rodape: string
}

/**
 * Memória de cálculo de uma linha. Pedido da Mariana em 17/09/2026:
 * "passar o mouse em cima da margem de contribuição do preço cheio e do preço promocional e ver
 * uma tabelinha com os valores envolvidos naquele cálculo".
 *
 * Não recalcula nada: mostra as parcelas que entraram, com a origem de cada uma. Parcela com
 * `valor: null` deve aparecer como "não entrou na conta" — nunca como R$ 0,00.
 *
 * `p` nulo = memória do preço cheio.
 */
export function memoriaCalculo(
  a: Anuncio,
  p: Promocao | null,
  base: BaseImposto,
  cmvManual?: Map<string, number>,
): MemoriaCalculo {
  const c = cmvDe(a, base, cmvManual)
  const cheio = p == null
  const preco = cheio ? a.preco_atual : p!.preco_promo
  const comissao = cheio ? a.comissao_cheio_rs : p!.comissao_sim
  const rebate = cheio ? null : p!.rebate_ml_rs
  const frete = cheio ? a.frete_atual_api : p!.frete_real
  const imposto = cheio
    ? (base === 'real' ? a.imposto_cheio_rs_real : a.imposto_cheio_rs)
    : impostoDaPromo(p!, base)
  const total = cheio ? mcCheioDe(a, base) : mcDe(a, p!, base, cmvManual)
  const aliq = aliquotaDoAd(a, base)
  const origemImp = cheio
    ? (origemImposto(a, base) ?? '—')
    : ((base === 'real' ? p!.imposto_origem_real : p!.imposto_origem) ?? '—')

  return {
    titulo: cheio ? 'M.C. no preço cheio' : `M.C. nesta promoção · ${p!.name || p!.type_label}`,
    parcelas: [
      {
        rotulo: 'Preço', valor: preco, sinal: '',
        origem: cheio ? 'preço de venda hoje (API)' : `preço da promoção · ${p!.origem_preco ?? ''}`,
      },
      {
        rotulo: 'Comissão', valor: comissao, sinal: '−',
        origem: a.comissao_pct_atual != null
          ? `listing_prices real neste preço (${a.comissao_pct_atual}% da categoria)`
          : 'listing_prices real neste preço',
      },
      // só aparece quando existe: a parte do ML volta como desconto na comissão (Smart/Pré-negociada)
      ...(rebate != null && rebate > 0
        ? [{
            rotulo: 'Parte do ML abatida da taxa', valor: rebate, sinal: '+',
            origem: `o ML banca ${p!.ml_pct ?? '?'}% do preço original e abate isso da sua comissão → taxa líquida ${
              comissao != null ? r2(comissao - rebate).toFixed(2) : '?'} (é assim que a comissão aparece em margens)`,
          }]
        : []),
      { rotulo: 'Frete', valor: frete, sinal: '−', origem: 'shipping_options/free real neste preço' },
      { rotulo: 'Custo (CMV)', valor: c.v, sinal: '−', origem: c.origem },
      {
        rotulo: 'Imposto', valor: imposto, sinal: '−',
        origem: `${aliq != null ? `${aliq}% · ` : ''}${origemImp}`,
      },
    ],
    total,
    rodape: `Base: ${ROTULO_BASE[base]}${base !== 'real' && a.cmv_ajuste_motivo ? ` · CMV ajustado: ${a.cmv_ajuste_motivo}` : ''}`,
  }
}

// ============================================================================================
// BASE DE IMPOSTO — o texto que explica qual está valendo
// ============================================================================================

/**
 * ⚠️ As duas alíquotas NÃO batem, e não deveriam — a diferença é o CRÉDITO de imposto.
 * Isso muda o diagnóstico inteiro, então a tela precisa dizer qual base está valendo.
 */
export function explicaBase(c: Conta, base: BaseImposto): string {
  const i = c.imposto
  const pp = (v: number | null | undefined) => (v == null ? null : `${v > 0 ? '+' : ''}${v.toFixed(2)}pp`)
  if (base !== 'real') {
    // `conta` vem como "NICKNAME (user_id)"; o regime de MG só existe na Extrema. O motor emite 0.0 (não null) na
    // matriz, então o `??` sozinho não cairia no fallback — por isso o regime só é lido quando é a Extrema.
    const extrema = c.conta.includes(CONTAS.find((x) => x.empresa === 'EXTREMA')!.id)
    const regime = extrema ? (i.ajuste_regime_mediano_pp ?? i.ajuste_incentivo_mediano_pp) : null
    const quando = i.ajustada_calculada_em ? new Date(i.ajustada_calculada_em) : null
    const hora = quando && !isNaN(quando.getTime())
      ? new Intl.DateTimeFormat('pt-BR', { timeZone: 'America/Sao_Paulo', day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit', hourCycle: 'h23' }).format(quando).replace(',', '')
      : null
    const versao = i.regra_versao || hora
      ? ` (${[i.regra_versao ? `regra ${i.regra_versao}` : null, hora ? `calculada em ${hora}` : null].filter(Boolean).join(', ')})`
      : ''
    const clausulaRegime = extrema ? ', mais o regime de MG da Extrema (ICMS economizado − crédito perdido na transferência)' : ''
    return `${ROTULO_BASE.ajustada} — a alíquota da nota menos os créditos (skill margem-ajustada-fiesta): PIS/COFINS efetivo = 9,25% × (venda − ICMS − DIFAL) − 9,25% × (taxa + frete)${clausulaRegime}, calculado pedido a pedido na coleção margens_ajustadas${versao}.${
      pp(i.ajuste_pis_cofins_mediano_pp) ? ` Ajuste típico nesta conta: PIS/COFINS ${pp(i.ajuste_pis_cofins_mediano_pp)}${pp(regime) ? `, regime ${pp(regime)}` : ''}.` : ''
    } Medida nas vendas de cada anúncio (${i.n_ajustada_propria}/${i.n_ads}); sem venda, média ponderada da categoria (${i.n_ajustada_categoria}), rotulada. É a base padrão e a que o motor usa.`
  }
  return `${ROTULO_BASE.real} — a alíquota bruta da nota (ICMS + DIFAL + PIS/COFINS ÷ receita), das vendas de cada anúncio; sem venda, a média ponderada da categoria, sempre rotulada. A ajustada fica acima dela pelo valor dos créditos — a diferença não é erro. Esta base é informativa: o motor decide na ajustada.`
}

// ============================================================================================
// ALERTA — promoção ativa rendendo pouco
// ============================================================================================

/** Corte pedido pela Mariana em 17/09/2026 (era 15). */
export const CORTE_MC_ALERTA = 16

export interface LinhaAlerta {
  a: Anuncio
  p: Promocao
  m: ResultadoMC
}

/**
 * Promoções ATIVAS cuja margem fica abaixo do corte, pior primeiro.
 *
 * `semConta` são as ativas cuja margem não fecha (falta custo, comissão ou frete). Elas ficam
 * FORA da lista e são contadas à parte: sem o custo não dá para afirmar que estão abaixo do
 * corte. Mostre esse número na tela — não o esconda nem o some com os outros.
 */
export function ativasComMcBaixa(
  c: Conta, base: BaseImposto, cmvManual?: Map<string, number>,
): { abaixo: LinhaAlerta[]; semConta: LinhaAlerta[] } {
  const abaixo: LinhaAlerta[] = []
  const semConta: LinhaAlerta[] = []
  for (const a of c.ads) {
    for (const p of a.promocoes) {
      if (p.status !== 'started') continue
      const m = mcDe(a, p, base, cmvManual)
      if (m.pct == null) { semConta.push({ a, p, m }); continue }
      if (m.pct < CORTE_MC_ALERTA) abaixo.push({ a, p, m })
    }
  }
  abaixo.sort((x, y) => (x.m.pct ?? 0) - (y.m.pct ?? 0))
  return { abaixo, semConta }
}

// ============================================================================================
// ABA ANÁLISE — "quem deveria estar em promoção e não está"
// ============================================================================================

/** Sem promoção = nenhuma ATIVA. Ter candidata não conta — é justamente o que dá para ativar. */
export const semPromocaoAtiva = (a: Anuncio) => !a.promocoes.some((p) => p.status === 'started')

/** A candidata de maior margem, para um anúncio que hoje não participa de nada. */
export function melhorDisponivel(
  a: Anuncio, base: BaseImposto, cmvManual?: Map<string, number>,
): { p: Promocao; m: ResultadoMC } | null {
  const cands = a.promocoes
    .filter((p) => p.status === 'candidate' || p.status === 'pending')
    .map((p) => ({ p, m: mcDe(a, p, base, cmvManual) }))
    .filter((x) => x.m.rs != null)
  if (!cands.length) return null
  return cands.reduce((best, x) => ((x.m.pct ?? 0) > (best.m.pct ?? 0) ? x : best))
}

export interface RecorteAnalise {
  id: 'curvaA' | 'novos' | 'full'
  titulo: string
  /** Rótulo da coluna numérica que varia por recorte. */
  colunaExtra: string
  ads: Anuncio[]
}

/**
 * Os três recortes pedidos pela Mariana em 17/09/2026. Todos olham só para anúncios sem
 * promoção ativa.
 *
 * Números medidos na base de 17/09 (matriz): Curva A 21, novos 50, Full 19.
 */
export function recortesAnalise(c: Conta): RecorteAnalise[] {
  const semPromo = c.ads.filter(semPromocaoAtiva)
  const dias = c.analise?.dias_novo ?? 60
  return [
    {
      id: 'curvaA',
      titulo: 'Curva A sem promoção',
      colunaExtra: 'Faturamento 30d',
      ads: semPromo.filter((a) => a.curva === 'A').sort((x, y) => y.faturamento_30d - x.faturamento_30d),
    },
    {
      id: 'novos',
      titulo: `Produtos novos (menos de ${dias} dias) sem promoção`,
      colunaExtra: 'Criado em',
      ads: semPromo.filter((a) => a.novo)
        .sort((x, y) => (y.date_created ?? '').localeCompare(x.date_created ?? '')),
    },
    {
      id: 'full',
      titulo: 'Produtos no Full sem promoção',
      colunaExtra: 'Faturamento 30d',
      ads: semPromo.filter((a) => a.logistic_type === 'fulfillment')
        .sort((x, y) => y.faturamento_30d - x.faturamento_30d),
    },
  ]
}

/**
 * Texto explicativo de cada recorte. Vale a pena manter na tela: é o que impede a analista de
 * ler "Curva A" como outra coisa.
 */
export function explicaRecorte(c: Conta, id: RecorteAnalise['id']): string {
  const an = c.analise
  switch (id) {
    case 'curvaA':
      return `Curva A = os anúncios que somam os primeiros ${Math.round((an?.curva_corte_a ?? 0.8) * 100)}% do faturamento dos últimos 30 dias desta conta. São ${an?.n_curva?.A ?? '—'} anúncios na curva A; os listados aqui não estão em promoção nenhuma. É o recorte de maior impacto.`
    case 'novos':
      return `Anúncios criados nos últimos ${an?.dias_novo ?? 60} dias, pela data de criação da API (date_created), que ainda não entraram em promoção nenhuma.`
    case 'full':
      return 'Anúncios com logistic_type = fulfillment, ou seja, com estoque parado no centro do Mercado Livre. Estoque no Full custa armazenagem enquanto não gira.'
  }
}

// ============================================================================================
// DIVERGÊNCIA DE ALÍQUOTA
// ============================================================================================

/**
 * ⚠️ NÃO reimplemente isto comparando planilha e ajustada cruamente.
 *
 * A planilha de alíquota por SKU NÃO entra mais na M.C. (18/09/2026). Ela fica como
 * conferência: a lista traz quem foge da diferença TÍPICA (ajustada − planilha) da própria conta
 * em mais de `imposto.limiar_alerta_pp` (8pp) — sinal de alíquota errada na planilha para aquele
 * SKU. Já vem calculado no JSON, ordenado do pior para o melhor — aqui é só leitura.
 */
export const divergenciasImposto = (c: Conta): DivergenciaImposto[] => c.alertas_divergencia_imposto ?? []

export function explicaDivergencia(c: Conta): string {
  const i = c.imposto
  if (i.divergencia_tipica_pp == null) {
    return 'Não dá para medir nesta conta: nenhum anúncio tem ao mesmo tempo alíquota própria na planilha (SKU) e alíquota ajustada medida das vendas dele.'
  }
  return `A planilha${i.fonte_planilha?.empresa ? ` de ${i.fonte_planilha.empresa}` : ''} não entra mais na M.C. (a base é a ajustada). Ela fica como conferência: nesta conta a ajustada fica em média ${i.divergencia_tipica_pp > 0 ? '+' : ''}${i.divergencia_tipica_pp.toFixed(1)}pp em relação à planilha, medido em ${i.divergencia_tipica_n} anúncios que têm as duas. A lista traz quem foge desse padrão em mais de ${i.limiar_alerta_pp}pp — costuma ser alíquota errada na planilha para aquele SKU.`
}

// ============================================================================================
// MARGEM SUSPEITA — acima de 25% é erro de CMV até prova em contrário
// ============================================================================================

/**
 * Regra da Mariana, 18/09/2026: "sempre que vc ver margens de contribuição acima de 25%
 * desconfie — certamente há erro no CMV". A Fiesta precifica para ~23%; muito acima disso quase
 * sempre é custo errado no cadastro. Fica na aba Análise, com o CMV e a origem dele. O motor
 * bloqueia esses anúncios ("Precisa de você"). Nunca apresentar como "anúncio ótimo".
 * Medido em 18/09: matriz 368 anúncios, Extrema 12.
 */
export const mcSuspeitas = (c: Conta): McSuspeita[] => c.alertas_mc_suspeita ?? []

/**
 * `travaLigada` = `fila.regras.revisao_margem_alta` (REVISAO_MARGEM_ALTA do motor). Em 01/10/2026 ela
 * conferiu os 292 anúncios acima de 25% ("estão corretos, são produtos que entraram em liquidação") e a
 * trava foi DESLIGADA: o motor trata esses anúncios como qualquer outro. A lista continua existindo como
 * informação (o CMV ainda pode estar errado em um caso novo), mas não é mais alerta nem "precisa de você".
 */
export function explicaMcSuspeita(c: Conta, travaLigada = true): string {
  const corte = c.mc_suspeita_pct ?? 25
  if (!travaLigada) {
    return `Trava DESLIGADA (regra do motor revisao_margem_alta = não): você conferiu estes anúncios em 01/10/2026 — são produtos em liquidação, com o custo certo — e o robô decide sobre eles como sobre qualquer outro. A lista fica só para consulta: um anúncio NOVO que apareça aqui ainda merece um olhar no CMV. Para o robô voltar a segurar estes casos (e o botão Liberar voltar à Revisão humana), religue a trava no motor.`
  }
  return `Regra sua (18/09/2026): margem de contribuição acima de ${corte}% é sinal de erro no custo, não de anúncio bom. Cada linha traz o CMV que entrou na conta e de onde ele veio — é isso que precisa ser conferido. Enquanto não for corrigido, o motor não decide sobre esses anúncios: eles estão na Revisão humana (aba Alertas), onde o botão Liberar (CMV conferido) os devolve ao robô.`
}

// ============================================================================================
// KPIs do topo
// ============================================================================================

export interface Kpi { label: string; value: string; hint: string; alerta?: boolean }

/**
 * ⚠️ CUIDADO com "M.C. incompleta" — o painel HTML original erra aqui, não copie o número dele.
 *
 * O `montar_dados.py` marca `mc_incompleta = bool(faltas)`, o que dá **1.489** na matriz, e o KPI
 * do HTML exibe esse número dizendo "em laranja nas tabelas". Não é verdade: **1.483 dessas nem
 * têm margem calculável** — aparecem como "sem simulação", não em laranja. Só **6** são de fato
 * "a margem existe mas está otimista", que é o que o laranja significa.
 *
 * Medido em 17/09/2026 rodando este arquivo contra o dado real. São duas coisas diferentes e a
 * tela deve separá-las:
 *   - laranja  = tem margem, mas faltou um componente → número otimista, dá para usar com ressalva
 *   - sem M.C. = não dá para calcular nada → não dá para usar
 */
export function kpis(c: Conta, base: BaseImposto, cmvManual?: Map<string, number>, travaMargemAlta = true): Kpi[] {
  const promos = c.ads.flatMap((a) => a.promocoes)
  const ativas = promos.filter((p) => p.status === 'started')
  const { abaixo, semConta } = ativasComMcBaixa(c, base, cmvManual)
  const todas = c.ads.flatMap((a) => a.promocoes.map((p) => mcDe(a, p, base, cmvManual)))
  const laranja = todas.filter((m) => m.rs != null && m.faltas.length).length
  const semMc = todas.filter((m) => m.rs == null).length
  const nf = (n: number) => n.toLocaleString('pt-BR')
  return [
    { label: 'Anúncios', value: nf(c.ads.length), hint: c.amostra },
    { label: 'Promoções', value: nf(promos.length), hint: `${campanhasDaConta(c).filter((x) => !x.encerrada).length} campanhas em curso` },
    { label: 'Promoções ativas', value: nf(ativas.length), hint: 'participando agora' },
    { label: 'Candidatas a entrar', value: nf(promos.filter((p) => p.status === 'candidate').length), hint: 'dá para ativar' },
    { label: `M.C. abaixo de ${CORTE_MC_ALERTA}%`, value: nf(abaixo.length), hint: `${semConta.length} ativas sem margem calculável`, alerta: true },
    { label: 'M.C. otimista (laranja)', value: nf(laranja), hint: 'margem existe, mas faltou um componente', alerta: true },
    { label: 'Sem M.C. calculável', value: nf(semMc), hint: 'não dá para simular', alerta: true },
    { label: 'Sem custo', value: nf(c.alertas_cmv_ausente.length), hint: 'CMV ausente no cadastro', alerta: true },
    { label: `M.C. acima de ${c.mc_suspeita_pct ?? 25}%`, value: nf(mcSuspeitas(c).length), hint: travaMargemAlta ? 'conferir o CMV (Revisão humana)' : 'liquidação — conferido por você (trava desligada)', alerta: travaMargemAlta },
  ]
}

// ============================================================================================
// CHAVES E COR — o que a tela usa em todas as abas
// ============================================================================================

/** Identidade de uma (anúncio, promoção) na tela: seleção, marcação e valor escolhido. */
export const selKey = (a: Pick<Anuncio, 'mlb'>, p: Pick<Promocao, 'id' | 'type'>) => `${a.mlb}|${p.id || p.type}`

/** Régua da casa: 20% "salvo raras exceções" (17/09/2026). A margem alvo é editável no topo. */
export const ALVO_PADRAO = 20

export type ClasseMc = 'pos' | 'mid' | 'neg' | 'inc' | ''

/**
 * Cor da M.C.: verde a partir do alvo, amarelo entre zero e o alvo, vermelho no negativo.
 * Laranja ('inc') quando o número existe mas faltou um componente — nunca confundir com vermelho.
 */
export function classeMc(m: ResultadoMC | null | undefined, alvo: number): ClasseMc {
  if (!m || m.pct == null) return ''
  if (m.faltas.length) return 'inc'
  return m.pct < 0 ? 'neg' : m.pct < alvo ? 'mid' : 'pos'
}

export const faltasTxt = (f: string[]) => (f.length ? `M.C. incompleta — falta ${f.join(', ')}` : '')

// ============================================================================================
// STATUS EFETIVO — a tela pode ter ativado/removido nesta sessão (ou marcado em preview)
// ============================================================================================

export interface StatusLocal {
  status: StatusPromo
  /** true = só marcação na tela (preview, sem serviço); false = escrita real confirmada pela API. */
  preview: boolean
}

/**
 * Devolve a conta com o status das promoções corrigido pelo que aconteceu nesta sessão.
 * Cria objetos novos só para o que mudou; o resto é a mesma referência. Assim TODAS as regras
 * (mcHoje, ativasComMcBaixa, recortes…) enxergam o estado atual sem precisar de parâmetro extra.
 */
export function aplicaStatusLocal(c: Conta, local: Map<string, StatusLocal>): Conta {
  if (!local.size) return c
  let mudou = false
  const ads = c.ads.map((a) => {
    let mudouAd = false
    const promocoes = a.promocoes.map((p) => {
      const s = local.get(selKey(a, p))
      if (!s || s.status === p.status) return p
      mudouAd = true
      return { ...p, status: s.status, status_label: s.status === 'started' ? (s.preview ? 'Ativa (preview)' : 'Participando') : 'Candidata' }
    })
    if (!mudouAd) return a
    mudou = true
    return { ...a, promocoes }
  })
  return mudou ? { ...c, ads } : c
}

// ============================================================================================
// FAIXA DE VALOR — só quem tem min/max/original REAIS na API ganha seletor. Nunca se inventa.
// ============================================================================================

export interface FaixaPromo {
  /** '%' = LIGHTNING e sub_type FLEXIBLE; 'R$' = PRICE_DISCOUNT e DEAL (escolha dela, 17/09). */
  unit: '%' | 'R$'
  min: number
  max: number
  def: number
  step: number
  original: number
  /** Faixa em PREÇO (a API sempre dá em preço), para travar o valor escolhido. */
  precoMin: number
  precoMax: number
}

/**
 * Regra por tipo em 02_documentacao_dados/api_mercado_livre.md ("Regra de negócio por tipo").
 * A porta de entrada: só entra quem tem min/max/original reais. Tipo novo sem faixa é aceite
 * direto — e nem abre o modal.
 */
export function promoRange(p: Promocao): FaixaPromo | null {
  if (p.min_discounted_price == null || p.max_discounted_price == null || !p.original_price) return null
  const pctUnit = p.type === 'LIGHTNING' || String(p.sub_type ?? '').includes('FLEXIBLE')
  const emPreco = p.type === 'PRICE_DISCOUNT' || p.type === 'DEAL'
  if (!pctUnit && !emPreco) return null
  const def = p.suggested_discounted_price ?? p.preco_promo ?? p.max_discounted_price
  const precoMin = Math.min(p.min_discounted_price, p.max_discounted_price)
  const precoMax = Math.max(p.min_discounted_price, p.max_discounted_price)
  const original = p.original_price
  if (pctUnit) {
    const toPct = (price: number) => r1((1 - price / original) * 100)
    return { unit: '%', min: toPct(precoMax), max: toPct(precoMin), def: toPct(def), step: 0.5, original, precoMin, precoMax }
  }
  return { unit: 'R$', min: precoMin, max: precoMax, def, step: 0.5, original, precoMin, precoMax }
}

export const rangeToPrice = (range: FaixaPromo, v: number) =>
  range.unit === '%' ? r2(range.original * (1 - v / 100)) : r2(v)

export const priceToRange = (range: FaixaPromo, preco: number) =>
  range.unit === '%' ? r1((1 - preco / range.original) * 100) : r2(preco)

/** Trava o preço dentro da faixa da API. */
export const clampPreco = (range: FaixaPromo, preco: number) =>
  r2(Math.min(range.precoMax, Math.max(range.precoMin, preco)))

export const pickerLabel = (range: FaixaPromo, v: number) =>
  range.unit === '%' ? `${v.toLocaleString('pt-BR', { minimumFractionDigits: 1, maximumFractionDigits: 1 })}%` : brlFmt(v)

// ============================================================================================
// M.C. NUM PREÇO QUALQUER — a única ESTIMATIVA da tela (modal e simulador), sempre rotulada
// ============================================================================================

export interface ResultadoEstimado extends ResultadoMC {
  estimativa: true
  comissao: number | null
  frete: number | null
  freteOrigem: string
  /** true = o frete não foi consultado neste preço; pode ter mudado de degrau. */
  freteAproximado: boolean
  imposto: number | null
  semImposto: boolean
  cmv: number | null
  cmvOrigem: string
}

/**
 * M.C. do anúncio a um preço escolhido. Comissão pela regra do degrau de R$12,50 (medida em
 * 24/08/2026: até R$12,50 há taxa fixa de 50%), porque não dá para chamar listing_prices a cada
 * tecla. Frete: o real consultado NESTE preço quando a tela já tem (serviço no ar); senão o da
 * promoção; senão o do preço cheio — e a tela diz qual está valendo. Imposto pela alíquota da base.
 */
export function mcNoPreco(
  a: Anuncio,
  p: Promocao | null,
  preco: number,
  base: BaseImposto,
  cmvManual?: Map<string, number>,
  freteRealNestePreco?: number | null,
): ResultadoEstimado {
  const c = cmvDe(a, base, cmvManual)
  const faltas: string[] = []
  const comissao = a.comissao_pct_atual == null
    ? null
    : preco <= 12.5
      ? r2(preco * 0.5 + (preco * a.comissao_pct_atual) / 100)
      : r2((preco * a.comissao_pct_atual) / 100)
  let frete: number | null
  let freteOrigem: string
  let freteAproximado = false
  if (freteRealNestePreco != null) {
    frete = freteRealNestePreco; freteOrigem = 'real neste preço (API)'
  } else if (p?.frete_real != null) {
    frete = p.frete_real; freteOrigem = 'real no preço desta promoção'; freteAproximado = true
  } else {
    frete = a.frete_atual_api; freteOrigem = 'real no preço cheio'; freteAproximado = true
  }
  const rate = aliquotaDoAd(a, base)
  const imposto = rate != null ? r2((preco * rate) / 100) : null
  if (comissao == null) faltas.push('comissão')
  if (frete == null) faltas.push('frete')
  if (c.v == null) faltas.push('custo')
  if (imposto == null) faltas.push('imposto')
  const vazio = { estimativa: true as const, comissao, frete, freteOrigem, freteAproximado, imposto, semImposto: imposto == null, cmv: c.v, cmvOrigem: c.origem }
  if (comissao == null || frete == null || c.v == null || preco <= 0) return { rs: null, pct: null, faltas, ...vazio }
  const rs = r2(preco + (p?.rebate_ml_rs ?? 0) - comissao - frete - c.v - (imposto ?? 0))
  return { rs, pct: r1((rs / preco) * 100), faltas, ...vazio }
}

// ============================================================================================
// ABA POR ANÚNCIO — "qual é a mais vantajosa para este anúncio?"
// ============================================================================================

export interface MelhorDoAnuncio {
  vencedora: { p: Promocao; m: ResultadoMC } | null
  /** false = só existe margem incompleta; a "vencedora" é aparente, não recomendação. */
  confiavel: boolean
  nCompletas: number
  nIncompletas: number
}

/**
 * Maior M.C. entre as promoções COMPLETAS do anúncio. Se só houver M.C. incompleta, não elege:
 * diz o que falta. Nunca compara número confiável com número otimista como se fossem iguais.
 */
export function melhorDoAnuncio(a: Anuncio, base: BaseImposto, cmvManual?: Map<string, number>): MelhorDoAnuncio {
  const comMc = a.promocoes.map((p) => ({ p, m: mcDe(a, p, base, cmvManual) })).filter((x) => x.m.rs != null)
  const completas = comMc.filter((x) => !x.m.faltas.length)
  const maior = (xs: typeof comMc) => [...xs].sort((x, y) => (y.m.rs ?? 0) - (x.m.rs ?? 0))[0] ?? null
  if (completas.length) return { vencedora: maior(completas), confiavel: true, nCompletas: completas.length, nIncompletas: comMc.length - completas.length }
  return { vencedora: maior(comMc), confiavel: false, nCompletas: 0, nIncompletas: comMc.length }
}

// ============================================================================================
// ESCRITA NA API — o payload que o serviço recebe (validado ao vivo em 18/09/2026)
// ============================================================================================

/** Tipos cujo valor já vem do ML: o POST leva só promotion_id. Mandar deal_price neles é inventar número. */
export const ACEITE_DIRETO = new Set(['SELLER_COUPON_CAMPAIGN', 'SMART', 'PRE_NEGOTIATED', 'PRICE_MATCHING', 'UNHEALTHY_STOCK'])

/** Duração do PRICE_DISCOUNT criado pela tela. A doc do ML dá 14 dias como teto; ela não fixou outro. */
export const DIAS_PRICE_DISCOUNT = 14

export interface PayloadAtivar {
  conta: string
  mlb: string
  promotion_type: string
  deal_price?: number
  promotion_id?: string
  /** SMART/PRE_NEGOTIATED: o `ref_id` da candidatura (validado ao vivo em 23/09/2026: 201). */
  offer_id?: string
  start_date?: string
  finish_date?: string
  /** Só LIGHTNING: unidades reservadas para a oferta (doc oficial do ML, "Ofertas relâmpago"). */
  stock?: number
}

/** Tipos em que o ML identifica a oferta por `offer_id` (co-participação e Pré Acordo). */
export const TIPOS_COM_OFFER_ID = new Set(['SMART', 'PRE_NEGOTIATED'])

/**
 * Unidades da relâmpago: regra dela de 18/09/2026 — "limitado a 5 unidades". Espelho de
 * `unidades_ofertadas()` em regras_promocoes.py: respeita o mínimo que o ML exige (`stock_min`,
 * normalmente 5) e o teto (`stock_max` ou o estoque do anúncio). Pedir mais do que se tem ou
 * menos que o mínimo é recusa certa na API.
 */
export const UNIDADES_RELAMPAGO = 5
export function unidadesRelampago(a: Anuncio, p: Promocao, pedido?: number | null): number {
  const minimo = p.stock_min ?? UNIDADES_RELAMPAGO
  const teto = p.stock_max ?? (a.estoque > 0 ? a.estoque : UNIDADES_RELAMPAGO)
  const alvo = pedido != null && pedido > 0 ? Math.floor(pedido) : UNIDADES_RELAMPAGO
  return Math.max(minimo, Math.min(alvo, teto))
}

const dataLocalSemFuso = (d: Date) => {
  const z = (n: number) => String(n).padStart(2, '0')
  return `${d.getFullYear()}-${z(d.getMonth() + 1)}-${z(d.getDate())}T${z(d.getHours())}:${z(d.getMinutes())}:${z(d.getSeconds())}`
}

/**
 * Regras descobertas no teste ao vivo de 18/09/2026:
 *  - deal_price só nos tipos com faixa; nos de aceite direto o serviço recusa.
 *  - promotion_id em toda campanha identificada (menos PRICE_DISCOUNT e LIGHTNING), sem o prefixo "OFFER-".
 *  - PRICE_DISCOUNT exige start/finish em hora LOCAL sem fuso; com offset ou "Z" o ML recusa.
 * Relâmpago (doc oficial "Ofertas relâmpago", lida em 23/09/2026): o corpo é
 * `{deal_price, stock, promotion_type: "LIGHTNING"}` — sem promotion_id, e `stock` é obrigatório.
 * `unidades` vem da fila do motor (`DecisaoMotor.unidades`); sem ela, a regra das 5.
 */
export function payloadAtivar(conta: string, a: Anuncio, p: Promocao, preco: number, agora = new Date(), unidades?: number | null): PayloadAtivar {
  const body: PayloadAtivar = { conta, mlb: a.mlb, promotion_type: p.type }
  if (!ACEITE_DIRETO.has(p.type)) body.deal_price = r2(preco)
  if (p.id && p.type !== 'PRICE_DISCOUNT' && p.type !== 'LIGHTNING') body.promotion_id = String(p.id).replace(/^OFFER-/, '')
  if (TIPOS_COM_OFFER_ID.has(p.type) && p.ref_id) body.offer_id = String(p.ref_id)
  if (p.type === 'PRICE_DISCOUNT') {
    body.start_date = dataLocalSemFuso(agora)
    body.finish_date = dataLocalSemFuso(new Date(agora.getTime() + DIAS_PRICE_DISCOUNT * 86400000 - 60000))
  }
  if (p.type === 'LIGHTNING') body.stock = unidadesRelampago(a, p, unidades)
  return body
}

/**
 * Identificadores que vão no DELETE. Medido no log do BI (22–23/09/2026): DEAL sem promotion_id
 * → "Promotion id is required"; SMART sem offer_id → "Offer id is required". A doc de
 * co-participação confirma: DELETE leva promotion_type + promotion_id + offer_id.
 * PRICE_DISCOUNT e LIGHTNING não têm campanha identificada — só o tipo.
 */
export function paramsParaRemover(p: Promocao): { promotion_id?: string; offer_id?: string } {
  const out: { promotion_id?: string; offer_id?: string } = {}
  if (p.id && p.type !== 'PRICE_DISCOUNT' && p.type !== 'LIGHTNING') out.promotion_id = String(p.id).replace(/^OFFER-/, '')
  if (TIPOS_COM_OFFER_ID.has(p.type) && p.ref_id) out.offer_id = String(p.ref_id)
  return out
}

/** @deprecated use paramsParaRemover — mantido para quem só mostra o promotion_id. */
export const promotionIdParaRemover = (p: Promocao) => paramsParaRemover(p).promotion_id

// ============================================================================================
// ABA APROVAÇÕES — rótulos das seções lidos dos pisos da fila, nunca escritos na tela
// ============================================================================================

export const decisaoKey = (d: DecisaoMotor) => `${d.mlb}|${d.promocao_id || d.promocao_tipo}`

export function rotuloAcao(fila: FilaDecisoes | null, acao: AcaoMotor): [string, string] {
  const r = fila?.regras
  const sai = r?.margem_saida_automatica ?? '?', ent = r?.margem_minima_entrar ?? '?'
  const ale = r?.margem_alerta ?? '?', rel = r?.margem_relampago ?? '?', sel = r?.margem_seletor ?? '?', max = r?.max_ativas_disputa ?? 3
  switch (acao) {
    case 'sair_auto': return [`🔻 Sair — margem abaixo de ${sai}%`, `Ativas rendendo menos de ${sai}%. O robô tira sozinho nas rodadas do dia, todas (sem teto por rodada), exceto relâmpago (o ML não deixa remover) e as suas exceções com prazo. Ativa sem margem confiável com desconto acima de 10% também sai. Cupom: anúncio em grupo só recebe os cupons do grupo; os gerais ativos saem pelo robô quando a saída de cupom estiver ligada na VPS.`]
    case 'entrar': return [`✅ Entrar — até ${max} ativas por anúncio (preço fixo ≥ ${ent}% · seletor a ${sel}%)`, `Entra em TODAS as campanhas que fecham a margem, até ${max} ativas por anúncio. Preço fixado pelo ML (SMART, Pré-Acordo): margem no preço dele ≥ ${ent}%. Seletor de desconto (DEAL, campanha do vendedor, desconto individual): o robô calcula o preço para ${sel}% de margem e só ativa se a faixa da campanha permitir. Campanha do vendedor e desconto individual preenchem as vagas que sobrarem, mas nunca as duas no mesmo anúncio. Ordem de entrada: piso primeiro; depois preço fixado pelo ML (SMART, Pré-Acordo) pela melhor margem, DEAL, campanha do vendedor, desconto individual. Cupom e relâmpago não competem: são camadas.`]
    case 'trocar': return ['🔄 Trocar — as vagas estão cheias e apareceu campanha mais vantajosa', 'Régua: rebate do ML → margem → menor preço para o cliente (sem ganho mínimo). São DUAS chamadas: sai da menos vantajosa, entra na nova — se a segunda falhar, a tela avisa que o anúncio ficou fora de tudo.']
    case 'relampago': return ['⚡ Relâmpago — conta inteira, 5 unidades', `Toda candidata que fecha o piso de relâmpago (${rel}% ou o do grupo) no preço proposto pelo ML entra (23/10/2026); os grupos marcados como lista fixa vêm primeiro, na ordem em que você os digitou. Já dentro vale por janela; o robô para a janela quando o ML recusa por vagas e aprende o limite.`]
    case 'cupom': return ['🎟️ Cupom — verba de marketing, todas as campanhas', 'Entra em 100% dos anúncios elegíveis, em qualquer situação (inclusive os em revisão humana) — menos os cupons exclusivos de um grupo; anúncio em grupo só recebe os cupons do grupo; os gerais ativos saem pelo robô. Fica abaixo da linha da M.C. — o desconto dele não entra na margem, e tem teto em R$ por campanha. Em grupo com cupom escolhido, a margem que decide as outras promoções do anúncio é a do preço com esse cupom aplicado.']
    case 'alertar': return [`⚠️ Revisão humana — ativa com margem entre ${sai}% e ${ale}%`, 'A promoção FICA ativa; decide você: sair, deixar, ou marcar exceção com prazo no painel "Exceções do robô" (queima de estoque, por exemplo). Ativa sem margem confiável com desconto até 10% também aparece aqui.']
    case 'bloqueado': return ['⛔ Robô não entra nesta promoção', 'Sem CMV, CMV absurdo, variações sem custo, desconto acima de 30% quando o preço é do robô, promoção ativa que puxaria o preço abaixo do piso, ou dado que a releitura ainda não fechou. Cupom entra mesmo assim; o resto espera você conferir o CMV. É daqui que sai a lista de correções da base.']
    default: return ['Nada a fazer', '']
  }
}

/** Agrupa os bloqueados pelo motivo, sem os números (R$ X, N%), para a lista ficar legível. */
export function bloqueadosPorMotivo(decs: DecisaoMotor[]): Array<[string, DecisaoMotor[]]> {
  const por = new Map<string, DecisaoMotor[]>()
  for (const d of decs) {
    const k = (d.motivo || '').replace(/R\$ [\d.,]+/g, 'R$ X').replace(/\d+(?:[.,]\d+)?%/g, 'N%').replace(/\d+×/g, 'N×').slice(0, 70)
    por.set(k, [...(por.get(k) ?? []), d])
  }
  return [...por.entries()].sort((x, y) => y[1].length - x[1].length)
}

// ============================================================================================
// SIMULADOR — preço livre; comissão pelo degrau, frete editável, imposto pela base
// ============================================================================================

export interface LinhaSimulacao { rotulo: string; valor: number | null; nota: string }
export interface Simulacao { linhas: LinhaSimulacao[]; mc: number | null; pct: number | null }

export function simular(a: Anuncio, preco: number, frete: number, base: BaseImposto, cmvManual?: Map<string, number>): Simulacao {
  const pctFee = a.comissao_pct_atual
  const comissao = pctFee == null ? null : preco <= 12.5 ? r2(preco * 0.5 + (preco * pctFee) / 100) : r2((preco * pctFee) / 100)
  const notaCom = pctFee == null ? 'sem % de comissão' : preco <= 12.5 ? 'taxa fixa 50% (≤ R$ 12,50)' : `só percentual (${pctFee}%)`
  const c = cmvDe(a, base, cmvManual)
  const aliq = aliquotaDoAd(a, base)
  const imposto = aliq != null ? r2((preco * aliq) / 100) : null
  const mc = comissao != null && c.v != null ? r2(preco - comissao - frete - c.v - (imposto ?? 0)) : null
  return {
    linhas: [
      { rotulo: 'Receita (preço simulado)', valor: preco, nota: '' },
      { rotulo: 'Comissão ML', valor: comissao, nota: notaCom },
      { rotulo: 'Frete', valor: frete, nota: 'real da API no preço atual — edite se o preço simulado for bem diferente' },
      { rotulo: 'CMV', valor: c.v, nota: c.v == null ? 'ausente — fora do cálculo' : c.origem },
      { rotulo: 'Imposto', valor: imposto, nota: aliq != null ? `${aliq}% · ${origemImposto(a, base) ?? ''}` : 'sem alíquota — fora da conta' },
    ],
    mc,
    pct: mc != null && preco > 0 ? r1((mc / preco) * 100) : null,
  }
}

// ============================================================================================
// TEXTO DA SELEÇÃO — o caminho de colar no chat continua existindo
// ============================================================================================

const brlFmt = (n: number) => n.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' })

export function textoSelecao(
  c: Conta, sel: Set<string>, pickerVal: Map<string, number>, base: BaseImposto, cmvManual?: Map<string, number>,
): string {
  const linhas: string[] = []
  for (const a of c.ads) {
    for (const p of a.promocoes) {
      const k = selKey(a, p)
      if (!sel.has(k)) continue
      const range = promoRange(p)
      const v = pickerVal.get(k)
      const escolhido = range && v != null ? `\tvalor escolhido: ${pickerLabel(range, v)}` : ''
      const m = mcDe(a, p, base, cmvManual)
      const aviso = m.faltas.length ? `\t⚠ MC incompleta (falta ${m.faltas.join(', ')})` : ''
      linhas.push(`${a.mlb}\t${a.titulo}\t${p.type_label}${p.name ? ` "${p.name}"` : ''}\tpreço ${p.preco_promo == null ? '—' : brlFmt(p.preco_promo)}\tMC ${m.rs == null ? '—' : `${brlFmt(m.rs)} (${m.pct}%)`}${escolhido}${aviso}`)
    }
  }
  return `Conta: ${c.conta}\nAtivar (${linhas.length}):\n${linhas.join('\n')}`
}

// ---------------------------------------------------------------------------------------------
// Exceções do robô (24/09/2026): a linha da fila pede o formulário do painel "Exceções do robô"
// ---------------------------------------------------------------------------------------------
// ---------------------------------------------------------------------------------------------
// Grupos com regra própria (ela, 05/10/2026): lista de anúncios com pisos e cupons exclusivos
// ---------------------------------------------------------------------------------------------
type GrupoBase = {
  id: string; nome: string; ate: string; piso: number; piso_relampago: number | null; piso_saida: number | null
  n_mlbs: number; cupons: string[]
  /** Grupos v2 (ela, 09/10/2026): piso do seletor (null = piso) e campanhas do grupo (entram mesmo futuras; desde 23/10/2026 respeitam todos os pisos).
   *  Ausentes em grupos/filas gravados antes de 09/10. */
  piso_seletor?: number | null
  campanhas?: string[]
  /** Lista fixa da relâmpago diária (ela, 21/10/2026): o robô liga a relâmpago só nos MLBs destes grupos, na ordem digitada. */
  relampago_diaria?: boolean
  /** Liquidação / catálogo / estoque parado (ela, 23/10/2026): exceção do item 5 (entra com MC ≥ piso sem superar a vencedora). */
  liquidacao?: boolean
  /** Preço-alvo (ela, 25-26/10/2026): teto do preço ao comprador (alvo × (1 + tolerância)); vale para os MLBs sem o seu. */
  preco_alvo?: number | null
  /** Proteção contra queima: opção abaixo disto não entra. */
  preco_minimo?: number | null
  /** Tolerância acima do alvo (%); null = a geral (Parâmetros, 1,5 %). */
  tolerancia_acima?: number | null
  /** Desconto individual que não foi o robô que criou nunca sai (padrão ligado). */
  respeitar_desconto_manual?: boolean
  /** Relâmpago só com a flag (lista fixa implica ligada). */
  permitir_relampago?: boolean
}
/** Grupo como o motor grava na fila (`fila.grupos`): traz `vencido`. */
export type GrupoFila = GrupoBase & {
  vencido: boolean
  /** Cupom único do grupo como o motor leu (ela, 06/10/2026); `falta` = dado que impede a conta (ex.: teto desconhecido). */
  cupom?: { id: string; nome: string; pct: number | null; teto: number | null; minimo: number | null; falta: string | null } | null
}
/** Grupo como o serviço devolve (GET /api/promocoes/grupos): sem `vencido` — o estado vem em `estado`. */
export type Grupo = GrupoBase & {
  estado: 'vigente' | 'vencendo' | 'vencido'; dias_restantes: number | null; motivo: string; cupons_nomes: string[]
  campanhas_nomes?: string[]
  mlbs: { mlb: string; titulo?: string; conta?: string; criado_em?: string; preco_alvo?: number | null }[]; por?: string
}
export type CupomConta = { id: string; nome: string; conta: string; n_ativa: number; n_candidata: number; n_ads: number }
/** Campanha de disputa da conta (GET /api/promocoes/campanhas), para marcar como campanha do grupo (09/10/2026). */
export type CampanhaConta = {
  id: string; nome: string; type: string; conta: string; start_date: string | null; finish_date: string | null
  n_ativa: number; n_candidata: number; n_ads: number
}
/** "Natal Decor · DEAL · 13/10→19/10" — rótulo da campanha no seletor e no cartão do grupo. */
export function rotuloCampanha(c: { nome: string; type?: string; start_date?: string | null; finish_date?: string | null }) {
  const dm = (s?: string | null) => (s ? `${s.slice(8, 10)}/${s.slice(5, 7)}` : '')
  const datas = c.start_date || c.finish_date ? ` · ${dm(c.start_date)}→${dm(c.finish_date)}` : ''
  return `${c.nome}${c.type ? ` · ${c.type}` : ''}${datas}`
}

/** Mesma regra do backend (normalizar_mlbs): separa por quebra de linha, vírgula, ponto e vírgula, espaço ou tab;
 *  maiúsculas; válido = MLB + 6 ou mais dígitos; sem repetidos, na ordem em que apareceram. */
export function parseMlbs(texto: string): { validos: string[]; invalidos: string[]; precos: Record<string, number> } {
  // 25/10/2026 (ela): a linha aceita o preço-alvo do MLB — `MLB7876232432;14,80`, `MLB 14,80` ou tab. Mesma regra de
  // core/grupos.normalizar_mlbs_com_preco: `;` seguido de número é preço; a vírgula só separa quando não está entre dígitos.
  const validos: string[] = []
  const invalidos: string[] = []
  const precos: Record<string, number> = {}
  const ehMlb = (t: string) => /^MLB\d{6,}$/.test(t)
  const ehPreco = (t: string) => /^\d+([.,]\d{1,2})?$/.test(t)
  for (const linha of texto.split('\n')) {
    const brutos = linha.replace(/;/g, ' ; ').split(/\s+/).map((t) => t.trim().toUpperCase()).filter(Boolean)
    const toks: string[] = []
    brutos.forEach((t, k) => {
      if (k && brutos[k - 1] === ';') toks.push(t)
      else toks.push(...t.split(/,(?!\d)|(?<!\d),/).filter(Boolean))
    })
    let i = 0
    while (i < toks.length) {
      const tok = toks[i++]
      if (tok === ';') continue
      if (!ehMlb(tok)) { if (!invalidos.includes(tok)) invalidos.push(tok); continue }
      let preco: number | null = null
      if (i < toks.length && toks[i] === ';' && i + 1 < toks.length && !ehMlb(toks[i + 1])) {
        const bruto = toks[i + 1]
        if (ehPreco(bruto)) { preco = parseFloat(bruto.replace(',', '.')); i += 2 }
        else if (/\d/.test(bruto)) { const marca = `${tok} (preço inválido)`; if (!invalidos.includes(marca)) invalidos.push(marca); i += 2; continue }
      } else if (i < toks.length && ehPreco(toks[i])) { preco = parseFloat(toks[i].replace(',', '.')); i += 1 }
      if (!validos.includes(tok)) validos.push(tok)
      if (preco != null) precos[tok] = preco
    }
  }
  return { validos, invalidos, precos }
}

/** Prévia por MLB do grupo (GET /api/promocoes/grupos/{id}/previa — ela, 25/10/2026, 2.7). */
export type PreviaGrupo = {
  ok: boolean; conta: string; gerado_em: string | null
  grupo: { id: string; nome: string; preco_alvo: number | null; ate: string }
  mlbs: Array<{
    mlb: string; titulo: string | null; preco_cheio: number | null
    promo_vista: { tipo: string; nome: string | null; preco: number } | null
    origem: 'robo' | 'manual' | null; preco_alvo: number | null
    proxima: Array<{ acao: string; motivo: string; regra: string | null; promocao_tipo: string | null; promocao_nome: string | null
      preco_promo: number | null; preco_alvo: number | null; margem: number | null; origem_promo: string | null
      preco_antes: number | null; preco_depois: number | null
      de: { tipo: string | null; preco: number | null } | null; para: { tipo: string | null; preco: number | null } }>
  }>
}

/** Id do grupo a partir do nome — IGUAL ao `slug` de services/promocoes/core/grupos.py: minúsculas, sem
 *  acentos, tudo que não é [a-z0-9] vira '-', sem '-' nas pontas. A tela reconhece "já existe" por ele. */
export function slugGrupo(nome: string): string {
  return (nome || '').normalize('NFKD').replace(/[^\x00-\x7F]/g, '').toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-+|-+$/g, '')
}

/** Pisos do grupo em uma linha (painel Grupos e rodapé) — "10% · seletor 12% · relâmpago 7% · saída nunca" */
export function rotuloPisos(g: { piso: number; piso_relampago: number | null; piso_saida: number | null; piso_seletor?: number | null }) {
  return `${g.piso}% · seletor ${g.piso_seletor ?? g.piso}%${g.piso_seletor == null ? ' (= piso)' : ''} · relâmpago ${g.piso_relampago ?? g.piso}%${g.piso_relampago == null ? ' (= piso)' : ''} · saída ${g.piso_saida != null ? `${g.piso_saida}%` : 'nunca'}`
}

/** Evento do botão "+ grupo" nas outras abas: detail { mlb, titulo? }. */
export const EVENTO_GRUPO = 'fiesta:promo-grupo'

export const EVENTO_EXCECAO = 'fiesta:promo-excecao'

let excecaoPendente: { mlb: string; titulo?: string } | null = null

/** Chamado pelas linhas da fila ("exceção"): pré-preenche o formulário do painel e rola até ele.
 *  O pedido fica guardado para o caso de o painel ainda não estar montado (botão da Revisão humana,
 *  na aba Alertas, que troca para a aba Robô no mesmo clique — 19/10/2026). */
export function pedirExcecao(mlb: string, titulo?: string) {
  excecaoPendente = { mlb, titulo }
  window.dispatchEvent(new CustomEvent(EVENTO_EXCECAO, { detail: { mlb, titulo } }))
}

/** O painel de exceções consome na montagem o pedido feito antes de ele existir na tela. */
export function consumirExcecaoPendente() {
  const x = excecaoPendente
  excecaoPendente = null
  return x
}
